import React, { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import SalesAffiliateStatusBanner from "../components/SalesAffiliateStatusBanner";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { supabase } from "../lib/supabaseClient";

const FUNCTION_NAME = "sales_affiliate_application";

const initialForm = {
  full_name: "",
  primary_industry: "",
  role_title: "",
  org_types_30_days: "",
  network_description: "",
};

const STATUS_CONTENT = {
  MANUAL_REVIEW_REQUIRED: {
    eyebrow: "Application received",
    title: "Application under review",
    statusLabel: "Under Review",
    summary:
      "Your application has been received successfully and is now in the review stage.",
    action:
      "No action is required from you right now. Please do not submit another application.",
    steps: [
      {
        title: "We review your application",
        description:
          "The Sales Affiliate team reviews the information you provided together with the screening result.",
      },
      {
        title: "We contact you if clarification is needed",
        description:
          "If additional information is required, the team will use the email address associated with your account.",
      },
      {
        title: "You receive the decision and next steps",
        description:
          "When the review is complete, you will be told whether you can proceed and what you need to do next.",
      },
    ],
  },
  PRE_ACTIVATION: {
    eyebrow: "Application approved",
    title: "Your application has advanced",
    statusLabel: "Pre-Activation",
    summary:
      "Your application has passed the review stage and your account is now in pre-activation.",
    action:
      "Complete each onboarding requirement when it becomes available in your account.",
    steps: [
      {
        title: "Review the Sales Affiliate Agreement",
        description:
          "Read and accept the program agreement before activation.",
      },
      {
        title: "Complete onboarding and training",
        description:
          "Finish the required orientation and assigned learning activities.",
      },
      {
        title: "Set up commission payments",
        description:
          "Payment setup can be completed now or later and does not block activation.",
      },
    ],
  },
  ACTIVE: {
    eyebrow: "Activation complete",
    title: "Your Sales Affiliate account is active",
    statusLabel: "Active",
    summary:
      "You have completed the required activation steps and may now use the approved Sales Affiliate tools.",
    action:
      "Continue to your Sales Affiliate workspace and follow the current program guidance.",
    steps: [
      {
        title: "Open your Sales Affiliate workspace",
        description:
          "Access your dashboard, learning materials, resources, and lead tools.",
      },
      {
        title: "Review current program guidance",
        description:
          "Confirm the latest policies, approved practices, and announcements.",
      },
      {
        title: "Begin approved Sales Affiliate activity",
        description:
          "Use the program tools to register and manage eligible opportunities.",
      },
    ],
  },
  DISAPPROVED: {
    eyebrow: "Application decision",
    title: "Your application review is complete",
    statusLabel: "Decision Complete",
    summary:
      "A final decision has been recorded for your Sales Affiliate application.",
    action:
      "Review the official communication associated with your application for any available next steps.",
    steps: [
      {
        title: "Review the decision",
        description:
          "Read the official application decision carefully.",
      },
      {
        title: "Follow any stated instructions",
        description:
          "Take action only when the decision includes a specific next step.",
      },
      {
        title: "Keep your account information current",
        description:
          "Maintain access to the email address associated with your account.",
      },
    ],
  },
  REGISTERED_UNVERIFIED: {
    eyebrow: "Application received",
    title: "Your application has been submitted",
    statusLabel: "Submitted",
    summary:
      "Your application was received successfully and has entered the Sales Affiliate application process.",
    action:
      "No additional submission is required. Return to this page for the latest status.",
    steps: [
      {
        title: "Your application is recorded",
        description:
          "The application has been securely associated with your account.",
      },
      {
        title: "Screening and review continue",
        description:
          "The required application checks and review steps are completed.",
      },
      {
        title: "Your status is updated",
        description:
          "This page will reflect the next stage when a new status is recorded.",
      },
    ],
  },
};

function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `sales-affiliate-application-${crypto.randomUUID()}`;
  }

  return `sales-affiliate-application-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function validateForm(form) {
  if (!form.full_name.trim()) return "Please enter your full name.";
  if (!form.primary_industry.trim()) return "Please enter your primary industry.";
  if (!form.role_title.trim()) return "Please enter your current role or title.";
  if (!form.org_types_30_days.trim()) {
    return "Please describe the organizations you can reach in the next 30 days.";
  }
  if (!form.network_description.trim()) {
    return "Please describe your professional network.";
  }
  return "";
}

function getLifecycleState(result) {
  return (
    result?.lifecycle_state ||
    result?.disposition_result?.lifecycle_state ||
    "REGISTERED_UNVERIFIED"
  );
}

function getStatusContent(lifecycleState) {
  return (
    STATUS_CONTENT[lifecycleState] ||
    STATUS_CONTENT.REGISTERED_UNVERIFIED
  );
}

function getStageState(lifecycleState, stageKey) {
  const stageOrder = [
    "SUBMITTED",
    "MANUAL_REVIEW_REQUIRED",
    "PRE_ACTIVATION",
    "ACTIVE",
  ];

  const lifecycleToStage = {
    REGISTERED_UNVERIFIED: "SUBMITTED",
    MANUAL_REVIEW_REQUIRED: "MANUAL_REVIEW_REQUIRED",
    PRE_ACTIVATION: "PRE_ACTIVATION",
    ACTIVE: "ACTIVE",
    DISAPPROVED: "MANUAL_REVIEW_REQUIRED",
  };

  const currentStage = lifecycleToStage[lifecycleState] || "SUBMITTED";
  const currentIndex = stageOrder.indexOf(currentStage);
  const stageIndex = stageOrder.indexOf(stageKey);

  if (stageIndex < currentIndex) return "complete";
  if (stageIndex === currentIndex) return "current";
  return "upcoming";
}

function StatusMarker({ state, isLast }) {
  const markerClass =
    state === "complete"
      ? "border-emerald-500 bg-emerald-500 text-white"
      : state === "current"
      ? "border-blue-600 bg-blue-600 text-white shadow-[0_0_0_8px_rgba(37,99,235,0.10)]"
      : "border-slate-300 bg-white text-transparent";

  const lineClass =
    state === "complete" ? "bg-emerald-400" : "bg-slate-300";

  return (
    <div className="relative flex w-16 flex-none justify-center">
      <div
        aria-hidden="true"
        className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-4 ${markerClass}`}
      >
        {state === "complete" ? (
          <span className="text-2xl font-black">✓</span>
        ) : state === "current" ? (
          <span className="h-4 w-4 rounded-full bg-white" />
        ) : (
          <span className="h-4 w-4 rounded-full bg-transparent" />
        )}
      </div>

      {!isLast ? (
        <div
          aria-hidden="true"
          className={`absolute left-1/2 top-14 h-[calc(100%+1.5rem)] w-1 -translate-x-1/2 ${lineClass}`}
        />
      ) : null}
    </div>
  );
}

function ApplicationStatus({ result }) {
  const lifecycleState = getLifecycleState(result);

  const stages = [
    {
      key: "SUBMITTED",
      title: "Submitted",
      description: "Your application has been received.",
    },
    {
      key: "MANUAL_REVIEW_REQUIRED",
      title: "Under Review",
      description: "We are reviewing your application.",
    },
    {
      key: "PRE_ACTIVATION",
      title: "Completing Onboarding",
      description: "Finish the remaining activation requirements.",
    },
    {
      key: "ACTIVE",
      title: "Active Sales Affiliate",
      description: "Your Sales Affiliate account is fully activated.",
    },
  ];

  return (
    <section
      aria-live="polite"
      aria-labelledby="application-status-title"
      className="space-y-5"
    >
      <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
        <h2
          id="application-status-title"
          className="text-3xl font-black tracking-tight text-slate-950"
        >
          Application Status
        </h2>

        <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.8fr)] lg:items-center">
          <div className="space-y-7">
            {stages.map((stage, index) => {
              const state = getStageState(lifecycleState, stage.key);
              const isCurrent = state === "current";

              return (
                <div key={stage.key} className="flex min-h-[104px] gap-5">
                  <StatusMarker
                    state={state}
                    isLast={index === stages.length - 1}
                  />

                  <div className="pt-1">
                    <div
                      className={`text-xl font-black tracking-tight sm:text-[2rem] ${
                        isCurrent ? "text-blue-600" : "text-slate-950"
                      }`}
                    >
                      {stage.title}
                    </div>
                    <p className="mt-2 text-base leading-7 text-slate-600 sm:text-lg">
                      {stage.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="rounded-3xl border border-slate-200 bg-slate-50 px-5 py-6 lg:px-6">
            <div className="text-sm font-black text-slate-950">What happens next</div>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              If more information is needed, we&apos;ll email you. Once our
              review is complete, we will explain how to get started.
            </p>
            <div className="mt-5 border-t border-slate-200 pt-4">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                Typical review time
              </div>
              <div className="mt-2 text-lg font-black text-slate-900">
                1–2 business days
              </div>
            </div>
          </aside>
        </div>
      </div>

      <details className="rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-sm sm:px-8">
        <summary className="cursor-pointer list-none text-base font-black text-slate-800">
          <span className="mr-3 text-xl">›</span>
          Application reference details
        </summary>
        <div className="mt-4 space-y-2 break-all border-t border-slate-100 pt-4 font-mono text-xs text-slate-600">
          <div>Affiliate ID: {result?.affiliate_id || "Not available"}</div>
          <div>
            Application ID: {result?.application_id || "Not available"}
          </div>
          <div>
            Application version:{" "}
            {result?.application_version ?? "Not available"}
          </div>
          <div>
            Submission state: {result?.submission_state || "Not available"}
          </div>
          <div>Lifecycle state: {lifecycleState}</div>
        </div>
      </details>
    </section>
  );
}

export default function SalesAffiliateApplication() {
  const navigate = useNavigate();
  const {
    loading,
    statusLoadFailed,
    errorMessage: workspaceError,
    workspaceData,
    configuration,
    refreshWorkspace,
  } = useSalesAffiliateWorkspace();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const idempotencyKey = useMemo(() => makeIdempotencyKey(), []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const validationError = validateForm(form);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
        body: {
          application_payload: {
            ...form,
            source_path: "/sales-affiliate/application",
            submitted_from: "authenticated_sales_affiliate_application_page",
          },
          idempotency_key: idempotencyKey,
        },
      });

      if (error) {
        throw new Error(
          error?.message || "Sales Affiliate application submission failed."
        );
      }

      if (!data?.ok) {
        throw new Error(
          data?.error || "Sales Affiliate application submission failed."
        );
      }

      setResult(data);
      await refreshWorkspace();

      if (
        data.lifecycle_state === "PRE_ACTIVATION" ||
        data.lifecycle_state === "ACTIVE"
      ) {
        navigate("/sales-affiliate/onboarding", { replace: true });
      }
    } catch (error) {
      setErrorMessage(
        error?.message || "Sales Affiliate application submission failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="application">
        <div className="flex min-h-[500px] items-center justify-center">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
            Loading application status…
          </div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="application">
        <div className="mx-auto flex min-h-[500px] max-w-2xl items-center px-8">
          <SalesAffiliateStatusBanner
            tone="warning"
            eyebrow="Status unavailable"
            title="We could not safely load your application status"
          >
            <p>
              The application form is temporarily hidden to prevent an
              accidental duplicate submission.
            </p>
            <button
              type="button"
              onClick={refreshWorkspace}
              className="mt-5 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white"
            >
              Retry status check
            </button>
            {workspaceError ? (
              <p className="mt-3 text-xs opacity-75">{workspaceError}</p>
            ) : null}
          </SalesAffiliateStatusBanner>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (!configuration.allowsApplicationPage) {
    return <Navigate to={configuration.defaultPath} replace />;
  }

  const persistedResult = workspaceData?.current_application || null;
  const displayResult = result || persistedResult;

  return (
    <SalesAffiliateLayout
      activeSection="application"

    >
      <div className="max-w-5xl mx-auto px-8 py-10">
          <div className="mb-8">
            <div className="text-xs font-black uppercase tracking-[0.35em] text-blue-600">
              AI Fine Protector Sales Affiliate Program
            </div>
            <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950">
              Sales Affiliate Application
            </h1>
            <p className="mt-3 max-w-3xl text-sm text-slate-600 leading-6">
              Apply to participate in the AI Fine Protector Sales Affiliate
              Program. Your application must complete the required review and
              activation stages before Sales Affiliate access is enabled.
            </p>
          </div>

          {displayResult ? (
            <ApplicationStatus result={displayResult} />
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-slate-700"
                    htmlFor="full_name"
                  >
                    Full name
                  </label>
                  <input
                    id="full_name"
                    name="full_name"
                    value={form.full_name}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="Jane Applicant"
                  />
                </div>

                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-slate-700"
                    htmlFor="primary_industry"
                  >
                    Primary industry
                  </label>
                  <input
                    id="primary_industry"
                    name="primary_industry"
                    value={form.primary_industry}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="Technology, insurance, healthcare, consulting..."
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    We use this information to help shape future industry-specific
                    training and support materials for Sales Affiliates.
                  </p>
                </div>

                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-slate-700"
                    htmlFor="role_title"
                  >
                    Current role or title
                  </label>
                  <input
                    id="role_title"
                    name="role_title"
                    value={form.role_title}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="Consultant, advisor, executive, founder..."
                  />
                </div>

                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-slate-700"
                    htmlFor="org_types_30_days"
                  >
                    Organizations you can reach in the next 30 days
                  </label>
                  <textarea
                    id="org_types_30_days"
                    name="org_types_30_days"
                    value={form.org_types_30_days}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="Describe the types of organizations or decision-makers you could realistically reach."
                  />
                </div>

                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-slate-700"
                    htmlFor="network_description"
                  >
                    Professional network description
                  </label>
                  <textarea
                    id="network_description"
                    name="network_description"
                    value={form.network_description}
                    onChange={handleChange}
                    rows={5}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="Describe your professional network and why it is relevant to AI governance risk conversations."
                  />
                </div>

                {errorMessage ? (
                  <div
                    role="alert"
                    className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
                  >
                    {errorMessage}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting
                    ? "Submitting..."
                    : "Submit Sales Affiliate Application"}
                </button>
              </form>
            </div>
          )}
      </div>
    </SalesAffiliateLayout>
  );
}
